import React, { useState, useEffect } from 'react';

const SMSQuestions = ({ activeConversation }) => {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Poll for SMS questions when conversation is active
  useEffect(() => {
    if (!activeConversation) {
      setQuestions([]);
      setSelectedQuestionId('');
      return;
    }

    const loadQuestions = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || '';
        
        // Check both the specific conversation and 'pending' for unassociated questions
        const conversationIds = [activeConversation.id, 'pending'];
        let allQuestions = [];
        
        for (const conversationId of conversationIds) {
          const endpoint = `${apiUrl}/api/sms-questions/conversation/${conversationId}`;
          console.log('Loading SMS messages from:', endpoint);
          
          const response = await fetch(endpoint);
          const data = await response.json();
          
          if (data.success && data.questions) {
            allQuestions = [...allQuestions, ...data.questions];
          }
        }
        
        // Remove duplicates and sort by timestamp
        const uniqueQuestions = allQuestions.reduce((acc, question) => {
          if (!acc.find(q => q.question_id === question.question_id)) {
            acc.push(question);
          }
          return acc;
        }, []);
        
        uniqueQuestions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setQuestions(uniqueQuestions);
        setLastUpdate(new Date());
        
        console.log(`Loaded ${uniqueQuestions.length} SMS messages`);
      } catch (error) {
        console.error('Error loading SMS messages:', error);
      } finally {
        setLoading(false);
      }
    };

    // Load immediately
    loadQuestions();

    // Poll every 3 seconds for new questions
    const interval = setInterval(loadQuestions, 3000);
    
    return () => clearInterval(interval);
  }, [activeConversation]);

  const handleSelectQuestion = async () => {
    if (!selectedQuestionId || !activeConversation || submitting) {
      return;
    }

    setSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/sms-questions/${selectedQuestionId}/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: activeConversation.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('Message selected:', data.text);
        
        // Remove the selected question from local state
        setQuestions(questions.filter(q => q.question_id !== selectedQuestionId));
        setSelectedQuestionId('');
        
        // Show success message temporarily
        //alert(`Message selected and sent to avatar: "${data.text}"`);
      } else {
        console.error('Failed to select SMS message:', data.error);
        alert('Error selecting message: ' + data.error);
      }
    } catch (error) {
      console.error('Error selecting SMS message:', error);
      alert('Error selecting message: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearQuestions = async () => {
    if (!activeConversation || clearing || questions.length === 0) {
      return;
    }

    if (!window.confirm(`Are you sure you want to clear all ${questions.length} SMS messages? This cannot be undone.`)) {
      return;
    }

    setClearing(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      // Clear questions for both conversation ID and 'pending'
      const conversationIds = [activeConversation.id, 'pending'];
      let totalDeleted = 0;
      
      for (const conversationId of conversationIds) {
        const response = await fetch(`${apiUrl}/api/sms-questions/conversation/${conversationId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        
        if (data.success) {
          totalDeleted += data.deletedCount || 0;
          console.log(`Cleared ${data.deletedCount} SMS messages for ${conversationId}`);
        } else {
          console.error('Failed to clear SMS messages:', data.error);
        }
      }
      
      // Clear local state
      setQuestions([]);
      setSelectedQuestionId('');
      
      if (totalDeleted > 0) {
        alert(`Cleared ${totalDeleted} SMS messages`);
      }
    } catch (error) {
      console.error('Error clearing SMS questions:', error);
      alert('Error clearing messages: ' + error.message);
    } finally {
      setClearing(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatPhone = (phone) => {
    // Format phone number for display
    if (!phone) return 'Unknown';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1);
      return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="sms-questions">
      <h3>Message Queue</h3>
      
      <div className="questions-header">
        <div className="status-info">
          <span className={`status-indicator ${questions.length > 0 ? 'has-messages' : 'no-messages'}`}>
            {questions.length > 0 ? `${questions.length} messages` : 'No messages'}
          </span>
          {lastUpdate && (
            <small>Last updated: {lastUpdate.toLocaleTimeString()}</small>
          )}
        </div>
        
        {questions.length > 0 && (
          <button 
            onClick={handleClearQuestions}
            disabled={clearing || !activeConversation}
            className="clear-all-btn"
          >
            {clearing ? 'Clearing...' : 'Clear All'}
          </button>
        )}
      </div>

      {!activeConversation && (
        <div className="no-conversation-warning">
          Create a session to view incoming messages
        </div>
      )}

      {activeConversation && (
        <div className="questions-container">
          {loading && questions.length === 0 && (
            <div className="loading">Loading messages...</div>
          )}

          {questions.length === 0 && !loading && (
            <div className="no-messages">
              <p>No messages available</p>
              <small>Messages sent to your SMS number will appear here</small>
            </div>
          )}

          {questions.length > 0 && (
            <div className="questions-list">
              <div className="questions-form">
                <h4>Select a message to forward:</h4>
                
                <div className="radio-group">
                  {questions.map((question) => (
                    <div key={question.question_id} className="question-option">
                      <input
                        type="radio"
                        id={question.question_id}
                        name="selectedQuestion"
                        value={question.question_id}
                        checked={selectedQuestionId === question.question_id}
                        onChange={(e) => setSelectedQuestionId(e.target.value)}
                      />
                      <label htmlFor={question.question_id} className="question-label">
                        <div className="question-content">
                          <div className="question-text">"{question.text}"</div>
                          <div className="question-meta">
                            <span className="question-from">From: {formatPhone(question.from_phone)}</span>
                            <span className="question-time">at {formatTime(question.timestamp)}</span>
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="action-buttons">
                  <button
                    onClick={handleSelectQuestion}
                    disabled={!selectedQuestionId || submitting || !activeConversation}
                    className="submit-question-btn"
                  >
                    {submitting ? 'Sending...' : 'Send Selected Message'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="help-text">
        <small>
          <strong>How it works:</strong> SMS messages sent to your phone number appear here. 
          Select one and click "Send Selected Message" to have your avatar respond to the message.
        </small>
      </div>
    </div>
  );
};

export default SMSQuestions;